import {
  PROPOSAL_KINDS_BY_TRADER_SIDE,
  REVIEWER_HINT_LIMITS,
  type DocumentProposalKind,
  type PrintedDocumentKind,
  type TraderSide
} from '@klear/constants/document-processing'
import type { ReviewerHints } from '@klear/schema/document-processing'
import { proposalKindLabel } from '@/app/document-review/review-labels'
import type { DocumentReviewDetailOutput } from '@/app/document-review/use-document-review'

export type PremiseDraft = {
  traderId: number | null
  traderName: string | null
  side: TraderSide | null
  note: string
}

type ConfirmedHint = NonNullable<ReviewerHints['confirmed']>[number]
type AnswerHint = NonNullable<ReviewerHints['answers']>[number]

export type CarriedHints = { confirmed: ConfirmedHint[]; answers: AnswerHint[]; orderConfirmed: boolean }

const PRINTED_DOCUMENT_KIND_LABELS: Record<PrintedDocumentKind, string> = {
  purchase_order: 'Purchase order',
  invoice: 'Invoice',
  credit_or_debit_memo: 'Credit or debit memo',
  change_to_existing_record: 'Change to an existing record',
  delivery_or_acceptance: 'Delivery or acceptance',
  something_else: 'Something else'
}

function createdKinds(detail: DocumentReviewDetailOutput): DocumentProposalKind[] {
  return detail.proposals.flatMap((proposal) =>
    proposal.record !== null && proposal.record.kind === 'create' ? [proposal.kind] : []
  )
}

export function sideOfRun(detail: DocumentReviewDetailOutput): TraderSide | null {
  const told = detail.reviewerHints?.traderSide
  if (told !== undefined) return told
  const kinds = createdKinds(detail)
  const buys = kinds.some((kind) => PROPOSAL_KINDS_BY_TRADER_SIDE.buys.includes(kind))
  const sells = kinds.some((kind) => PROPOSAL_KINDS_BY_TRADER_SIDE.sells.includes(kind))
  if (buys === sells) return null
  return buys ? 'buys' : 'sells'
}

function pageNoun(detail: DocumentReviewDetailOutput, documentKind: PrintedDocumentKind | null): string {
  if (documentKind !== null && documentKind !== 'something_else') {
    return PRINTED_DOCUMENT_KIND_LABELS[documentKind].toLowerCase()
  }
  const first = createdKinds(detail)[0]
  return first === undefined ? 'document' : proposalKindLabel(first)
}

export type PremiseWord = { text: string; was: string | null }

function capitalised(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

function sideSentence(trader: string, side: TraderSide | null): string {
  return side === null ? '' : `${trader} ${side}.`
}

export function premiseWords(detail: DocumentReviewDetailOutput, draft: PremiseDraft | null): PremiseWord[] {
  const runTrader = detail.traderName ?? 'no trader'
  const runSide = sideOfRun(detail)
  const noun = capitalised(pageNoun(detail, detail.reviewerHints?.documentKind ?? null))
  const trader = draft?.traderName ?? runTrader
  const side = draft === null ? runSide : (draft.side ?? (draft.traderId === detail.traderId ? runSide : null))
  const changed = (now: string, before: string): PremiseWord => ({ text: now, was: now === before ? null : before })
  const sideNow = sideSentence(trader, side)
  const sideBefore = sideSentence(runTrader, runSide)
  return [
    { text: `${noun} for `, was: null },
    changed(trader, runTrader),
    { text: '.', was: null },
    ...(sideNow === '' ? [] : [{ text: ' ', was: null }]),
    changed(sideNow, sideBefore)
  ].filter((word) => word.text !== '' || word.was !== null)
}

export function premiseText(detail: DocumentReviewDetailOutput): string {
  return premiseWords(detail, null)
    .map((word) => word.text)
    .join('')
}

export function draftFrom(detail: DocumentReviewDetailOutput): PremiseDraft {
  const told = detail.reviewerHints
  return {
    traderId: detail.traderId,
    traderName: detail.traderName,
    side: told?.traderSide ?? null,
    note: told?.note ?? ''
  }
}

export function withTrader(draft: PremiseDraft, traderId: number, traderName: string): PremiseDraft {
  if (traderId === draft.traderId) return draft
  return { ...draft, traderId, traderName, side: null }
}

function keyOf(one: ConfirmedHint): string {
  return `${one.reference ?? ''}\u0000${one.kind}\u0000${one.field}`
}

function fitsConfirmed(one: ConfirmedHint): boolean {
  return (
    (one.reference ?? '').length <= REVIEWER_HINT_LIMITS.reference && one.value.length <= REVIEWER_HINT_LIMITS.value
  )
}

function fitsAnswer(one: AnswerHint): boolean {
  return (
    (one.field ?? '').length <= REVIEWER_HINT_LIMITS.field &&
    one.message.length <= REVIEWER_HINT_LIMITS.message &&
    (one.answer ?? '').length <= REVIEWER_HINT_LIMITS.answer
  )
}

function mergeConfirmed(carried: ConfirmedHint[], earlier: readonly ConfirmedHint[]): ConfirmedHint[] {
  const now = new Set(carried.map(keyOf))
  return [...earlier.filter((one) => !now.has(keyOf(one))), ...carried]
}

function mergeAnswers(carried: AnswerHint[], earlier: readonly AnswerHint[]): AnswerHint[] {
  const key = (one: AnswerHint) => `${one.doubtKind}\u0000${one.field ?? ''}\u0000${one.message}`
  const now = new Set(carried.map(key))
  return [...earlier.filter((one) => !now.has(key(one))), ...carried]
}

export function hintsFrom(
  detail: DocumentReviewDetailOutput,
  draft: PremiseDraft,
  carried: CarriedHints
): ReviewerHints {
  const told = detail.reviewerHints
  const sameTrader = draft.traderId === detail.traderId
  const planOrder = detail.order
  const keepsOrder =
    sameTrader && (planOrder === null || (planOrder.kind === 'existing' && planOrder.orderId === told?.orderId))
  const confirmedOrder =
    sameTrader && carried.orderConfirmed && planOrder?.kind === 'existing' ? planOrder.orderId : undefined
  const orderId = confirmedOrder ?? (keepsOrder ? told?.orderId : undefined)
  const confirmed = mergeConfirmed(carried.confirmed.filter(fitsConfirmed), told?.confirmed ?? []).slice(
    -REVIEWER_HINT_LIMITS.confirmed
  )
  const answers = mergeAnswers(carried.answers.filter(fitsAnswer), told?.answers ?? []).slice(
    -REVIEWER_HINT_LIMITS.answers
  )
  const note = draft.note.trim()
  const planParents = detail.proposals.flatMap((proposal) =>
    proposal.record?.kind === 'create' && proposal.record.parentOrderEventId !== null
      ? [proposal.record.parentOrderEventId]
      : []
  )
  const toldParent = told?.parentOrderEventId
  const keepsParent =
    keepsOrder && toldParent !== undefined && (planParents.length === 0 || planParents.includes(toldParent))
  const planAttaches = detail.proposals.flatMap((proposal) =>
    proposal.record?.kind === 'attach' ? [proposal.record.orderEventId] : []
  )
  const toldRecord = told?.orderEventId
  const keepsRecord =
    keepsOrder && toldRecord !== undefined && (planAttaches.length === 0 || planAttaches.includes(toldRecord))
  return {
    ...(draft.side !== null && { traderSide: draft.side }),
    ...(sameTrader && told?.documentKind !== undefined && { documentKind: told.documentKind }),
    ...(orderId !== undefined && { orderId }),
    ...(keepsOrder && told?.childOrderEventId !== undefined && { childOrderEventId: told.childOrderEventId }),
    ...(keepsParent && { parentOrderEventId: toldParent }),
    ...(keepsRecord && { orderEventId: toldRecord }),
    ...(confirmed.length > 0 && { confirmed }),
    ...(answers.length > 0 && { answers }),
    ...(note !== '' && { note })
  }
}
